import { getEvaluationRules } from '../../evaluation-rules-load';
import type { PostureOrientationClassificationStrategy } from './posture-orientation-strategy-interface';
import type {
  PostureClassificationStrategyContext,
  PostureClassificationStrategyInput,
  PostureClassificationStrategyResult,
  PostureOrientationLabel,
} from './posture-orientation-strategy-types';
import { parseLateralPostureMetrics } from './posture-analysis-metrics-parse';
import {
  MAX_ORIENTATION_POINTS,
  sagittalFromLandmarks,
  scoreApt,
} from './hip-displacement-horizontal-strategy';
import type { OrientationScore } from './hip-displacement-horizontal-strategy';
import { logPostureClassificationDebug } from './posture-classification-debug';
import type { Landmark } from '@/components/posture-analysis/types';
import { IDX } from '@/components/posture-analysis/constants';

const STRATEGY_ID = 'adaptiveThresholdVprOverrideV6';
const STRATEGY_NAME =
  '20260912 Adaptive Threshold VPR Override v6 (mild hip-forward hyperext + posterior-knee swayback)';
const DEBUG_LOG_PREFIX = '[adaptiveThresholdVprOverrideV6]';

const MAX_AUTO_POINTS = 9; // Raised because of the locked-knee boost

interface AdaptiveVprV6Thresholds {
  mildAnteriorTiltDeg: number;
  hardCCurveVetoCm: number;
  softCCurvePenaltyCm: number;
  cCurvePenaltyPoints: number;
  lockedKneesHeadForwardCvDeg: number;
  swaybackShapeHipFwdMaxCm: number;
  swaybackShapeTorsoMinCm: number;
  tibialStrongDeg: number;
  kneeFlexionScoringExtremeDeg: number;
  plcKneeHipAntMinCm: number;
  plcHeadKneeMaxHipFwdCm: number;
  tibialGuardMinTorsoOffsetCm: number;
  mildHyperextAsFlexKneeHipPostMinCm: number;
}

type CoreClassification = {
  result: PostureClassificationStrategyResult;
  finalScoreSwayback: number;
  finalScoreApt: number;
};

const DEFAULT_V6_THRESHOLDS: AdaptiveVprV6Thresholds = {
  mildAnteriorTiltDeg: 3.0,
  hardCCurveVetoCm: -5.0,
  softCCurvePenaltyCm: -2.5,
  cCurvePenaltyPoints: 2,
  lockedKneesHeadForwardCvDeg: 14.0,
  swaybackShapeHipFwdMaxCm: 2.5,
  swaybackShapeTorsoMinCm: -1.0,
  tibialStrongDeg: 8.0,
  kneeFlexionScoringExtremeDeg: 8.0,
  plcKneeHipAntMinCm: -1.8,
  plcHeadKneeMaxHipFwdCm: 2.0,
  tibialGuardMinTorsoOffsetCm: -1.0,
  mildHyperextAsFlexKneeHipPostMinCm: -2.0,
};

function readV6Thresholds(rules: any): AdaptiveVprV6Thresholds {
  const v = rules?.adaptiveVprV6 ?? {};
  return {
    mildAnteriorTiltDeg: v.mildAnteriorTiltDeg ?? DEFAULT_V6_THRESHOLDS.mildAnteriorTiltDeg,
    hardCCurveVetoCm: v.hardCCurveVetoCm ?? DEFAULT_V6_THRESHOLDS.hardCCurveVetoCm,
    softCCurvePenaltyCm: v.softCCurvePenaltyCm ?? DEFAULT_V6_THRESHOLDS.softCCurvePenaltyCm,
    cCurvePenaltyPoints: v.cCurvePenaltyPoints ?? DEFAULT_V6_THRESHOLDS.cCurvePenaltyPoints,
    lockedKneesHeadForwardCvDeg:
      v.lockedKneesHeadForwardCvDeg ?? DEFAULT_V6_THRESHOLDS.lockedKneesHeadForwardCvDeg,
    swaybackShapeHipFwdMaxCm:
      v.swaybackShapeHipFwdMaxCm ?? DEFAULT_V6_THRESHOLDS.swaybackShapeHipFwdMaxCm,
    swaybackShapeTorsoMinCm:
      v.swaybackShapeTorsoMinCm ?? DEFAULT_V6_THRESHOLDS.swaybackShapeTorsoMinCm,
    tibialStrongDeg: v.tibialStrongDeg ?? DEFAULT_V6_THRESHOLDS.tibialStrongDeg,
    kneeFlexionScoringExtremeDeg:
      v.kneeFlexionScoringExtremeDeg ?? DEFAULT_V6_THRESHOLDS.kneeFlexionScoringExtremeDeg,
    plcKneeHipAntMinCm: v.plcKneeHipAntMinCm ?? DEFAULT_V6_THRESHOLDS.plcKneeHipAntMinCm,
    plcHeadKneeMaxHipFwdCm:
      v.plcHeadKneeMaxHipFwdCm ?? DEFAULT_V6_THRESHOLDS.plcHeadKneeMaxHipFwdCm,
    tibialGuardMinTorsoOffsetCm:
      v.tibialGuardMinTorsoOffsetCm ?? DEFAULT_V6_THRESHOLDS.tibialGuardMinTorsoOffsetCm,
    mildHyperextAsFlexKneeHipPostMinCm:
      v.mildHyperextAsFlexKneeHipPostMinCm ?? DEFAULT_V6_THRESHOLDS.mildHyperextAsFlexKneeHipPostMinCm,
  };
}

function resolveMildAnteriorTiltDeg(
  _clientSex: 'Male' | 'Female' | 'Unknown',
  mildAnteriorTiltDeg: number
): number {
  return mildAnteriorTiltDeg;
}

function hipFwdHyperextMagnitudeDeg(
  parsed: { maxKneeHyperextDeg?: number; maxKneeFlexDeg?: number },
  kneeFlexEvidenceDeg: number | undefined
): number {
  if (typeof parsed.maxKneeHyperextDeg === 'number' && parsed.maxKneeHyperextDeg > 0) {
    return parsed.maxKneeHyperextDeg;
  }
  if (typeof kneeFlexEvidenceDeg === 'number' && isFinite(kneeFlexEvidenceDeg)) {
    return kneeFlexEvidenceDeg;
  }
  if (typeof parsed.maxKneeFlexDeg === 'number' && parsed.maxKneeFlexDeg > 0) {
    return parsed.maxKneeFlexDeg;
  }
  return NaN;
}

/**
 * Forward-hip swayback geometry often places the knee posterior to the hip in image
 * space, so fromLm labels a flexed knee as hyperextended. Reinterpret that as flexion
 * when APT corroborators are absent (Kay extreme ≥ 5°, or Conor-08 mild hyperext with
 * clearly posterior knees and weak tibial). Unlabeled Conor ~2° without posterior
 * knee-hip, and Salvador tibial/FHP, stay hyperext.
 */
function shouldTreatHipFwdHyperextAsFlex(args: {
  kneeHyperext: boolean;
  hipFwdCm: number;
  hyperextDeg: number;
  tibialInclination: number;
  kneeHipAntCm: number;
  tibialAntThresholdDeg: number;
  isLockedKneesHeadForward: boolean;
  pelvicTilt: number;
  pelvicTiltVetoDegMin: number;
  v6: AdaptiveVprV6Thresholds;
  extremeHyperextDegMin: number;
}): boolean {
  if (!args.kneeHyperext) return false;
  if (!(args.hipFwdCm >= args.v6.swaybackShapeHipFwdMaxCm)) return false;
  const isTibialStrong =
    isFinite(args.tibialInclination) && args.tibialInclination >= args.v6.tibialStrongDeg;
  const pelvicOverrideAnterior =
    isFinite(args.pelvicTilt) && args.pelvicTilt >= args.pelvicTiltVetoDegMin;
  if (isTibialStrong || args.isLockedKneesHeadForward || pelvicOverrideAnterior) return false;

  const extreme =
    isFinite(args.hyperextDeg) && args.hyperextDeg >= args.extremeHyperextDegMin;
  if (extreme) return true;

  const tibialIsAptBand =
    isFinite(args.tibialInclination) && args.tibialInclination >= args.tibialAntThresholdDeg;
  const posteriorKnee =
    isFinite(args.kneeHipAntCm) &&
    args.kneeHipAntCm <= args.v6.mildHyperextAsFlexKneeHipPostMinCm;
  return (
    isFinite(args.hyperextDeg) &&
    args.hyperextDeg < args.extremeHyperextDegMin &&
    posteriorKnee &&
    !tibialIsAptBand
  );
}

interface VetoThresholds {
  slrSwaybackVetoMaxDeg: number;
  kneeFlexionExtremeDegMin: number;
  tibialAntKneeFlexGuardDeg: number;
  fullHipExtensionDeg: number;
  oberNeutralDeg: number;
  kneeHyperextensionExtremeDegMin: number;
  pelvicTiltVetoDegMin: number;
  overridePointBoost: number;
}

const DEFAULT_VETO_THRESHOLDS: VetoThresholds = {
  slrSwaybackVetoMaxDeg: 45,
  kneeFlexionExtremeDegMin: 10,
  tibialAntKneeFlexGuardDeg: 7.0,
  fullHipExtensionDeg: 0,
  oberNeutralDeg: 0,
  kneeHyperextensionExtremeDegMin: 5.0,
  pelvicTiltVetoDegMin: 15.0,
  overridePointBoost: 3,
};

function readVetoThresholds(rules: any): VetoThresholds {
  const v = rules?.vetoExclusion ?? {};
  return {
    slrSwaybackVetoMaxDeg: v.slrSwaybackVetoMaxDeg ?? DEFAULT_VETO_THRESHOLDS.slrSwaybackVetoMaxDeg,
    kneeFlexionExtremeDegMin:
      v.kneeFlexionExtremeDegMin ?? DEFAULT_VETO_THRESHOLDS.kneeFlexionExtremeDegMin,
    tibialAntKneeFlexGuardDeg:
      v.tibialAntKneeFlexGuardDeg ?? DEFAULT_VETO_THRESHOLDS.tibialAntKneeFlexGuardDeg,
    fullHipExtensionDeg: v.fullHipExtensionDeg ?? DEFAULT_VETO_THRESHOLDS.fullHipExtensionDeg,
    oberNeutralDeg: v.oberNeutralDeg ?? DEFAULT_VETO_THRESHOLDS.oberNeutralDeg,
    kneeHyperextensionExtremeDegMin:
      v.kneeHyperextensionExtremeDegMin ?? DEFAULT_VETO_THRESHOLDS.kneeHyperextensionExtremeDegMin,
    pelvicTiltVetoDegMin: v.pelvicTiltVetoDegMin ?? DEFAULT_VETO_THRESHOLDS.pelvicTiltVetoDegMin,
    overridePointBoost: v.overridePointBoost ?? DEFAULT_VETO_THRESHOLDS.overridePointBoost,
  };
}

const sumDefined = (...vals: (number | null | undefined)[]): number | null => {
  const finite = vals.filter((v): v is number => typeof v === 'number' && isFinite(v));
  return finite.length ? finite.reduce((a, b) => a + b, 0) : null;
};

const avgDefined = (...vals: (number | null | undefined)[]): number | null => {
  const finite = vals.filter((v): v is number => typeof v === 'number' && isFinite(v));
  return finite.length ? finite.reduce((a, b) => a + b, 0) / finite.length : null;
};

const bothAtLeast = (
  a: number | null | undefined,
  b: number | null | undefined,
  threshold: number
): boolean =>
  typeof a === 'number' &&
  isFinite(a) &&
  typeof b === 'number' &&
  isFinite(b) &&
  a >= threshold &&
  b >= threshold;

const bothAtMost = (
  a: number | null | undefined,
  b: number | null | undefined,
  threshold: number
): boolean =>
  typeof a === 'number' &&
  isFinite(a) &&
  typeof b === 'number' &&
  isFinite(b) &&
  a <= threshold &&
  b <= threshold;

const rad2deg = (r: number) => (r * 180) / Math.PI;

function angleAtLandmark(a: Landmark, b: Landmark, c: Landmark): number {
  if (!a || !b || !c) return NaN;
  const ab = { x: a.x - b.x, y: a.y - b.y };
  const cb = { x: c.x - b.x, y: c.y - b.y };
  const dot = ab.x * cb.x + ab.y * cb.y;
  const mab = Math.hypot(ab.x, ab.y);
  const mcb = Math.hypot(cb.x, cb.y);
  if (mab === 0 || mcb === 0) return NaN;
  const cos = Math.min(1, Math.max(-1, dot / (mab * mcb)));
  return rad2deg(Math.acos(cos));
}

function scoreSwaybackAdaptive(
  hipFwdCm: number,
  torsoOffsetCm: number,
  kneeFlex: boolean,
  pelvicTilt: number,
  rules: any,
  v6: AdaptiveVprV6Thresholds,
  kneeFlexEvidenceDeg?: number,
  kneeHipAntCm?: number,
  pelvicTiltDegMin?: number,
  mildAnteriorTiltDeg: number = DEFAULT_V6_THRESHOLDS.mildAnteriorTiltDeg,
  tibialInclination?: number,
  isPlcHeadKneePattern?: boolean,
  isPinned: boolean = true
): OrientationScore {
  const ev: string[] = [];
  const activeSignals: string[] = [];
  let pts = 0;
  const { hipForwardCmMin, strictHip, trunkLeanLatDegMin, strictLean, strictTilt } = rules;

  if (hipFwdCm >= hipForwardCmMin) {
    pts += 2;
    activeSignals.push('sb.hipFwd.markedAnterior');
    ev.push(`[+2] Hip markedly anterior to ankle plumb (${hipFwdCm.toFixed(1)} cm ≥ ${hipForwardCmMin} cm)`);
  } else if (hipFwdCm >= strictHip) {
    pts += 1;
    activeSignals.push('sb.hipFwd.mildAnterior');
    ev.push(`[+1] Hip anterior to ankle plumb (${hipFwdCm.toFixed(1)} cm ≥ ${strictHip} cm)`);
  }

  if (torsoOffsetCm <= -trunkLeanLatDegMin) {
    pts += 2;
    activeSignals.push('sb.torso.posteriorShouldersStrong');
    ev.push(`[+2] Shoulders posterior to hips vs plumb ("C" curve), Δ ≈ ${torsoOffsetCm.toFixed(1)} cm`);
  } else if (torsoOffsetCm <= -strictLean) {
    pts += 1;
    activeSignals.push('sb.torso.posteriorShouldersMild');
    ev.push(`[+1] Mild posterior shoulder shift vs hips (Δ ≈ ${torsoOffsetCm.toFixed(1)} cm)`);
  }

  // --- Pelvic tilt estimated guard (Swayback Penalty) ---
  if (isFinite(pelvicTilt) && pelvicTilt <= -strictTilt) {
    const isEstimated = !isPinned;
    const isKneeLockedOrPosterior = (!kneeFlex) || (typeof kneeHipAntCm === 'number' && kneeHipAntCm < 0);
    if (isEstimated && isKneeLockedOrPosterior) {
      activeSignals.push('sb.pelvis.posteriorTilt.suppressedByLockedOrPosteriorKnee');
      ev.push(
        `[+0] Estimated pelvic tilt suggests posterior tilt (~${pelvicTilt.toFixed(1)}°) but suppressed as Swayback sign because knees are locked or posterior (typical of APT locked-knee compensation)`
      );
    } else {
      pts += 1;
      activeSignals.push('sb.pelvis.posteriorTilt');
      ev.push(`[+1] Pelvic line suggests posterior tilt (~${pelvicTilt.toFixed(1)}°)`);
    }
  }

  // Robust pelvic tilt check: if it is inferred (fake 4°), don't trigger the guard
  // unless supported by strongly anterior tibial inclination or tibial inclination >= knee flexion.
  const isTibialStrong =
    typeof tibialInclination === 'number' &&
    isFinite(tibialInclination) &&
    tibialInclination >= v6.tibialStrongDeg;
  const isTibialGreaterThanKnee =
    typeof tibialInclination === 'number' &&
    isFinite(tibialInclination) &&
    typeof kneeFlexEvidenceDeg === 'number' &&
    isFinite(kneeFlexEvidenceDeg) &&
    tibialInclination >= kneeFlexEvidenceDeg;

  const pelvicTiltIsFullAnterior =
    typeof pelvicTiltDegMin === 'number' &&
    isFinite(pelvicTilt) &&
    pelvicTilt >= pelvicTiltDegMin;

  const isSwaybackShapeExcluded =
    hipFwdCm < v6.swaybackShapeHipFwdMaxCm &&
    torsoOffsetCm >= v6.swaybackShapeTorsoMinCm;

  const pelvicTiltIsMildAnterior =
    !pelvicTiltIsFullAnterior &&
    (isSwaybackShapeExcluded ||
      isPlcHeadKneePattern ||
      isTibialGreaterThanKnee ||
      (isFinite(pelvicTilt) &&
        (pelvicTilt >= mildAnteriorTiltDeg && (pelvicTilt >= 5.0 || isTibialStrong))));

  const degStr =
    typeof kneeFlexEvidenceDeg === 'number' && isFinite(kneeFlexEvidenceDeg)
      ? kneeFlexEvidenceDeg.toFixed(0)
      : null;

  // Do not suppress knee flexion if it is extremely severe (YAML kneeFlexionScoringExtremeDeg)
  const isKneeFlexionExtreme =
    typeof kneeFlexEvidenceDeg === 'number' &&
    kneeFlexEvidenceDeg >= v6.kneeFlexionScoringExtremeDeg;

  if (kneeFlex && pelvicTiltIsFullAnterior && !isKneeFlexionExtreme) {
    activeSignals.push('sb.knee.flexion.suppressedByAnteriorPelvicTilt');
    ev.push(
      degStr
        ? `[+0] Knee flexion (~${degStr}°) suppressed as Swayback signal: pelvic tilt is anterior (~${pelvicTilt.toFixed(1)}° ≥ ${pelvicTiltDegMin}°)`
        : `[+0] Knee flexion suppressed as Swayback signal`
    );
  } else if (kneeFlex && pelvicTiltIsMildAnterior && !isKneeFlexionExtreme) {
    pts += 1;
    activeSignals.push('sb.knee.flexion.partiallySuppressedByMildAnteriorTilt');
    ev.push(
      degStr
        ? `[+1] Knee flexion (~${degStr}°) partially suppressed: pelvic tilt is mildly anterior (~${pelvicTilt.toFixed(1)}°)`
        : `[+1] Knee flexion partially suppressed`
    );
  } else if (kneeFlex) {
    pts += 2;
    activeSignals.push('sb.knee.flexion');
    ev.push(
      degStr
        ? `[+2] Knee flexion beyond ${rules.kneeFlexionDegMin}° (~${degStr}°)`
        : `[+2] Knee flexion beyond ${rules.kneeFlexionDegMin}°`
    );
  }

  const kneeHipSbMin = rules.kneeHipAnteriorSwaybackCmMin;
  if (
    typeof kneeHipSbMin === 'number' &&
    typeof kneeHipAntCm === 'number' &&
    isFinite(kneeHipAntCm) &&
    kneeHipAntCm > kneeHipSbMin &&
    !pelvicTiltIsFullAnterior &&
    !pelvicTiltIsMildAnterior
  ) {
    pts += 1;
    activeSignals.push('sb.kneeHip.anterior');
    ev.push(`[+1] Knees anterior to hips (${kneeHipAntCm.toFixed(1)} cm > ${kneeHipSbMin} cm)`);
  }

  return { pts: Math.min(MAX_ORIENTATION_POINTS, pts), evidence: ev, activeSignals };
}

function augmentScores(
  input: PostureClassificationStrategyInput,
  context: PostureClassificationStrategyContext
): {
  sb: { pts: number; evidence: string[]; activeSignals: string[] };
  apt: { pts: number; evidence: string[]; activeSignals: string[] };
  dynamicMax: number;
  parsed: any;
  fromLm: any;
  rules: any;
  v6: AdaptiveVprV6Thresholds;
  isPlcHeadKneePattern: boolean;
  isLockedKneesHeadForward: boolean;
  hasPlc: boolean;
  kneeHyperext: boolean;
  kneeFlex: boolean;
  kneeFlexDeg: number;
  hipFwdHyperextReinterpreted: boolean;
} | null {
  const rules = (getEvaluationRules() as any).posture.thresholds;
  const v6 = readV6Thresholds(rules);
  const kneeThresholds = {
    kneeFlexionDegMin: rules.kneeFlexionDegMin,
    kneeHyperextensionDegMin: rules.kneeHyperextensionDegMin,
  };
  const parsed = parseLateralPostureMetrics(input.analysisData, kneeThresholds);
  if (!parsed) return null;

  const avgCvAngle = parsed.avgCvAngle;
  const tibialInclination = parsed.tibialInclination;
  const pelvicTiltDegMin =
    input.clientSex === 'Female' ? rules.pelvicTiltDegMinFemale : rules.pelvicTiltDegMinMale;

  const fromLm = sagittalFromLandmarks(
    input,
    context,
    kneeThresholds.kneeFlexionDegMin,
    kneeThresholds.kneeHyperextensionDegMin
  );
  const hipFwdCm = fromLm?.hipFwdCm ?? parsed.hipFwdVal;
  const torsoOffsetCm = fromLm?.torsoOffsetCm ?? parsed.sagittalTorsoOffset;
  const kneeHyperext = fromLm
    ? (fromLm.kneeHyperext || (parsed.isKneeHyperext && !parsed.isKneeNeutral))
    : (parsed.isKneeHyperext && !parsed.isKneeNeutral);
  const kneeFlex = fromLm ? fromLm.kneeFlex : parsed.isKneeFlexed;
  const pelvicTilt = fromLm && isFinite(fromLm.pelvicTilt) ? fromLm.pelvicTilt : parsed.pelvicTilt;
  const kneeFlexEvidenceDeg =
    fromLm?.kneeFlexEvidenceDeg ??
    (isFinite(parsed.maxKneeFlexDeg) ? parsed.maxKneeFlexDeg : (kneeFlex ? 5.0 : undefined));
  const kneeHipAntCm =
    fromLm && isFinite(fromLm.kneeHipAntCm) ? fromLm.kneeHipAntCm : parsed.kneeHipAntCm;

  const isPinned =
    !!context.asisPsisLine ||
    (!!input.sagittal?.custom?.ASIS && !!input.sagittal?.custom?.PSIS) ||
    (isFinite(pelvicTilt) && !parsed.isPelvicTiltEstimated);

  const mildAnteriorTiltDeg = resolveMildAnteriorTiltDeg(input.clientSex, v6.mildAnteriorTiltDeg);

  // PLC Upgrade rules loaded from YAML
  const plcHeadFhpMinDeg = rules.plcHeadFhpMinDeg ?? 10.0;
  const plcKneeFlexMinDeg = rules.plcKneeFlexMinDeg ?? 3.0;
  const plcKneeHipAntMinCm = v6.plcKneeHipAntMinCm;
  const plcHeadKneeMaxHipFwdCm = v6.plcHeadKneeMaxHipFwdCm;

  const isLockedKneesHeadForward =
    isFinite(avgCvAngle) &&
    avgCvAngle >= v6.lockedKneesHeadForwardCvDeg &&
    !kneeFlex;

  const vetoForReinterpret = readVetoThresholds(rules);
  const hyperextDeg = hipFwdHyperextMagnitudeDeg(parsed, kneeFlexEvidenceDeg);
  let effectiveKneeHyperext = kneeHyperext;
  let effectiveKneeFlex = kneeFlex;
  let effectiveKneeFlexEvidenceDeg = kneeFlexEvidenceDeg;
  let hipFwdHyperextReinterpreted = false;
  if (
    shouldTreatHipFwdHyperextAsFlex({
      kneeHyperext,
      hipFwdCm,
      hyperextDeg,
      tibialInclination,
      kneeHipAntCm,
      tibialAntThresholdDeg: rules.tibialAntThresholdDeg ?? 3,
      isLockedKneesHeadForward,
      pelvicTilt,
      pelvicTiltVetoDegMin: vetoForReinterpret.pelvicTiltVetoDegMin,
      v6,
      extremeHyperextDegMin: vetoForReinterpret.kneeHyperextensionExtremeDegMin,
    })
  ) {
    effectiveKneeHyperext = false;
    effectiveKneeFlex = true;
    effectiveKneeFlexEvidenceDeg = isFinite(hyperextDeg) ? hyperextDeg : 5.0;
    hipFwdHyperextReinterpreted = true;
  }

  const kneeFlexVal =
    typeof effectiveKneeFlexEvidenceDeg === 'number'
      ? effectiveKneeFlexEvidenceDeg
      : effectiveKneeFlex
        ? 5.0
        : 0.0;

  const aptBase = scoreApt(
    hipFwdCm,
    torsoOffsetCm,
    effectiveKneeHyperext,
    pelvicTilt,
    rules,
    input.clientSex,
    kneeHipAntCm
  );

  // Tibial inclination >= knee flexion (mechanical indicator of posterior pelvic/lower cage compression)
  const isPlcTibialPattern =
    effectiveKneeFlex &&
    isFinite(tibialInclination) &&
    isFinite(kneeFlexVal) &&
    tibialInclination >= kneeFlexVal &&
    tibialInclination >= (rules.tibialAntThresholdDeg ?? 3) &&
    isFinite(kneeHipAntCm) &&
    kneeHipAntCm >= plcKneeHipAntMinCm;

  // Head forward + knee flex pattern
  const isPlcHeadKneePattern =
    isFinite(avgCvAngle) &&
    avgCvAngle >= plcHeadFhpMinDeg &&
    kneeFlexVal >= plcKneeFlexMinDeg &&
    isFinite(kneeHipAntCm) &&
    kneeHipAntCm >= plcKneeHipAntMinCm &&
    (effectiveKneeFlex || kneeHipAntCm >= 0.0) &&
    (hipFwdCm < plcHeadKneeMaxHipFwdCm ||
      (isFinite(tibialInclination) && isFinite(kneeFlexVal) && tibialInclination >= kneeFlexVal));

  const hasPlc = isPlcHeadKneePattern || isPlcTibialPattern;
  const isPlcOrLockedHeadForward = hasPlc || isLockedKneesHeadForward;

  const sbBase = scoreSwaybackAdaptive(
    hipFwdCm,
    torsoOffsetCm,
    effectiveKneeFlex,
    pelvicTilt,
    rules,
    v6,
    effectiveKneeFlexEvidenceDeg,
    kneeHipAntCm,
    hipFwdHyperextReinterpreted ? vetoForReinterpret.pelvicTiltVetoDegMin : pelvicTiltDegMin,
    mildAnteriorTiltDeg,
    tibialInclination,
    isPlcOrLockedHeadForward,
    isPinned
  );

  let sbPts = sbBase.pts;
  let aptPts = aptBase.pts;
  const sbEvidence: string[] = [...sbBase.evidence];
  const aptEvidence: string[] = [...aptBase.evidence];
  const sbSignals: string[] = [...sbBase.activeSignals];
  const aptSignals: string[] = [...aptBase.activeSignals];

  // --- Locked Knees Point Boost for APT (Relaxed without hip shift restriction) ---
  const isAptTiltOrHeadFwdForBoost =
    isLockedKneesHeadForward ||
    (isFinite(pelvicTilt) && pelvicTilt >= pelvicTiltDegMin) ||
    (isFinite(torsoOffsetCm) && torsoOffsetCm > rules.strictLean);

  if (!effectiveKneeFlex && (aptPts > 0 || isLockedKneesHeadForward) && isAptTiltOrHeadFwdForBoost) {
    aptPts += 2;
    aptSignals.push('vpr.apt.knee.lockedOrHyperextended');
    aptEvidence.push(`[+2] Locked/neutral knee state (typical anterior pelvic tilt compensation)`);
  } else if (!effectiveKneeFlex) {
    aptEvidence.push(`[INFO] Locked/neutral knee state present but APT boost not applied because no active anterior pelvic tilt or forward lean indicators are present`);
  }

  // --- Bidirectional Forward Head Posture ---
  const fhpMin = rules.fhpSwaybackDegMin ?? 10;
  const isTibialStrong = isFinite(tibialInclination) && tibialInclination >= v6.tibialStrongDeg;
  const isTibialGreaterThanKnee =
    isFinite(tibialInclination) &&
    typeof effectiveKneeFlexEvidenceDeg === 'number' &&
    isFinite(effectiveKneeFlexEvidenceDeg) &&
    tibialInclination >= effectiveKneeFlexEvidenceDeg;

  const isSwaybackShapeExcluded =
    hipFwdCm < v6.swaybackShapeHipFwdMaxCm &&
    torsoOffsetCm >= v6.swaybackShapeTorsoMinCm;

  const pelvicTiltIsMildlyAnterior =
    isSwaybackShapeExcluded ||
    isPlcOrLockedHeadForward ||
    isTibialGreaterThanKnee ||
    (isFinite(pelvicTilt) && (pelvicTilt >= mildAnteriorTiltDeg && (pelvicTilt >= 5.0 || isTibialStrong)));
  const pelvicTiltIsFullyAnterior =
    isFinite(pelvicTilt) && pelvicTilt >= pelvicTiltDegMin;

  if (isFinite(avgCvAngle) && avgCvAngle >= fhpMin) {
    if (pelvicTiltIsMildlyAnterior) {
      aptPts += 1;
      aptSignals.push('vpr.apt.forwardHeadPostureWithAnteriorTilt');
      aptEvidence.push(
        `[+1] Forward head posture (CV angle ~${avgCvAngle.toFixed(1)}° ≥ ${fhpMin}°) with anterior pelvis representation → lordotic APT compensation`
      );
    } else {
      sbPts += 1;
      sbSignals.push('vpr.sb.forwardHeadPosture');
      sbEvidence.push(
        `[+1] Forward head posture (CV angle ~${avgCvAngle.toFixed(1)}° ≥ ${fhpMin}°; typical swayback presentation)`
      );
    }
  } else if (
    isFinite(avgCvAngle) &&
    avgCvAngle < fhpMin &&
    pelvicTiltIsFullyAnterior
  ) {
    aptPts += 1;
    aptSignals.push('vpr.apt.neutralHeadPosture');
    aptEvidence.push(
      `[+1] Neutral/mild head posture (CV ~${avgCvAngle.toFixed(1)}° < ${fhpMin}°) with anterior pelvic tilt (~${pelvicTilt.toFixed(1)}°); supports APT`
    );
  }

  // --- Tibial Inclination ---
  const tibPostMin = rules.tibialPostThresholdDeg ?? 3;
  const tibAntMin = rules.tibialAntThresholdDeg ?? 3;
  if (isFinite(tibialInclination) && tibialInclination <= -tibPostMin) {
    sbPts += 1;
    sbSignals.push('vpr.sb.posteriorTibialInclination');
    sbEvidence.push(
      `[+1] Posterior tibial inclination (~${tibialInclination.toFixed(1)}° ≤ −${tibPostMin}°; knee flexion compensation)`
    );
  } else if (isFinite(tibialInclination) && tibialInclination >= tibAntMin) {
    aptPts += 1;
    aptSignals.push('vpr.apt.anteriorTibialInclination');
    aptEvidence.push(
      `[+1] Anterior tibial inclination (~${tibialInclination.toFixed(1)}° ≥ ${tibAntMin}°; hyperextension compensation)`
    );
  }

  // --- Tibial Inclination vs Knee Flexion Rule for APT/PLC ---
  if (isFinite(tibialInclination) && typeof effectiveKneeFlexEvidenceDeg === 'number' && isFinite(effectiveKneeFlexEvidenceDeg) && tibialInclination >= effectiveKneeFlexEvidenceDeg) {
    aptPts += 2;
    aptSignals.push('vpr.apt.tibialGreaterThanKneeFlex');
    aptEvidence.push(
      `[+2] Anterior tibial inclination (~${tibialInclination.toFixed(1)}°) ≥ knee flexion (~${effectiveKneeFlexEvidenceDeg.toFixed(0)}°) → strongly favors APT/PLC representation`
    );
  }

  // --- Head Forward with Flexed Knees (PLC/LPC pattern) Boost for APT ---
  if (isPlcHeadKneePattern) {
    aptPts += 2;
    aptSignals.push('vpr.apt.plcHeadKneePattern');
    aptEvidence.push(
      `[+2] Head forward (~${avgCvAngle.toFixed(1)}°) with flexed knees (~${effectiveKneeFlexEvidenceDeg?.toFixed(0)}°) in line/ahead of pelvis → indicates APT with PLC/LPC compensation`
    );
  }

  // --- Head Forward with Locked Knees Boost for APT ---
  if (isLockedKneesHeadForward) {
    aptPts += 2;
    aptSignals.push('vpr.apt.lockedKneesHeadForward');
    aptEvidence.push(
      `[+2] Head forward (~${avgCvAngle.toFixed(1)}°) with locked/neutral knees → indicates APT pattern rather than Swayback`
    );
  }

  // --- Practitioner visual observations ---
  const obs = input.practitionerObservations;
  let toggledCount = 0;

  if (obs?.poochBelly) {
    toggledCount += 1;
    sbPts += 1;
    sbSignals.push('vpr.sb.obs.poochBelly');
    sbEvidence.push('[+1] Practitioner observation: pooch belly (swayback)');
  }
  if (obs?.flatGlutes) {
    toggledCount += 1;
    sbPts += 1;
    sbSignals.push('vpr.sb.obs.flatGlutes');
    sbEvidence.push('[+1] Practitioner observation: flat/absent glutes (swayback)');
  }
  if (obs?.ribFlare) {
    toggledCount += 1;
    aptPts += 1;
    aptSignals.push('vpr.apt.obs.ribFlare');
    aptEvidence.push('[+1] Practitioner observation: rib flare (APT)');
  }
  if (obs?.flatCervicalCurve) {
    toggledCount += 1;
    aptPts += 1;
    aptSignals.push('vpr.apt.obs.flatCervicalCurve');
    aptEvidence.push(
      '[+1] Practitioner observation: flat/reduced cervical curve (APT)'
    );
  }

  const dynamicMax = MAX_AUTO_POINTS + toggledCount;

  return {
    sb: {
      pts: Math.min(dynamicMax, sbPts),
      evidence: sbEvidence,
      activeSignals: sbSignals,
    },
    apt: {
      pts: Math.min(dynamicMax, aptPts),
      evidence: aptEvidence,
      activeSignals: aptSignals,
    },
    dynamicMax,
    parsed,
    fromLm,
    rules,
    v6,
    isPlcHeadKneePattern,
    isLockedKneesHeadForward,
    hasPlc,
    kneeHyperext: effectiveKneeHyperext,
    kneeFlex: effectiveKneeFlex,
    kneeFlexDeg:
      typeof effectiveKneeFlexEvidenceDeg === 'number' && isFinite(effectiveKneeFlexEvidenceDeg)
        ? effectiveKneeFlexEvidenceDeg
        : NaN,
    hipFwdHyperextReinterpreted,
  };
}

function classifyFusedView(
  input: PostureClassificationStrategyInput,
  context: PostureClassificationStrategyContext
): CoreClassification | null {
    const rulesBundle = getEvaluationRules() as any;
    const rules = rulesBundle.posture.thresholds;
    const veto = readVetoThresholds(rules);

    const augmented = augmentScores(input, context);
    if (!augmented) return null;

    const {
      sb,
      apt,
      dynamicMax,
      parsed,
      fromLm,
      v6,
      isPlcHeadKneePattern,
      hasPlc: imageHasPlc,
      kneeHyperext,
      kneeFlex,
      kneeFlexDeg,
      hipFwdHyperextReinterpreted,
    } = augmented;

    const hipFwdCm = fromLm?.hipFwdCm ?? parsed.hipFwdVal;
    const torsoOffsetCm = fromLm?.torsoOffsetCm ?? parsed.sagittalTorsoOffset;
    const pelvicTilt = fromLm && isFinite(fromLm.pelvicTilt) ? fromLm.pelvicTilt : parsed.pelvicTilt;

    let kneeHyperextDeg = NaN;
    if (fromLm && input.sagittal?.landmarks?.length) {
      const s = input.sagittal;
      const side = s.sideView;
      const idxLR = (l: number, r: number) => (side === 'Left' ? l : r);
      const H = s.landmarks[idxLR(IDX.L_HIP, IDX.R_HIP)];
      const K = s.landmarks[idxLR(IDX.L_KNEE, IDX.R_KNEE)];
      const A = s.landmarks[idxLR(IDX.L_ANK, IDX.R_ANK)];
      if (H && K && A && Math.abs(A.y - H.y) > 10) {
        const kneeRawAngle = angleAtLandmark(H, K, A);
        const kneeDeg = isFinite(kneeRawAngle) ? Math.abs(180 - kneeRawAngle) : NaN;
        kneeHyperextDeg = kneeDeg;
      }
    }
    if (!isFinite(kneeHyperextDeg)) {
      kneeHyperextDeg = parsed.maxKneeHyperextDeg;
    }

    const isPinned =
      !!context.asisPsisLine ||
      (!!input.sagittal?.custom?.ASIS && !!input.sagittal?.custom?.PSIS) ||
      (isFinite(pelvicTilt) && !parsed.isPelvicTiltEstimated);

    let isSwaybackExcluded = false;
    let isAptExcluded = false;
    let forceStacked = false;
    let hasPlc = imageHasPlc || false;
    const overrideAndVetoEvidence: string[] = [];
    if (hipFwdHyperextReinterpreted) {
      overrideAndVetoEvidence.push(
        '[INFO] Image: hip-forward hyperextension with no APT corroborators → treated as flexion (fromLm hip-vs-knee geometry)'
      );
    }

    const sop = input.sopMetrics;
    if (sop) {
      const hipExtensionFullBilateral = bothAtLeast(
        sop.hipExtensionLeft,
        sop.hipExtensionRight,
        veto.fullHipExtensionDeg
      );
      const obersNeutralBilateral = bothAtMost(sop.oberLeft, sop.oberRight, veto.oberNeutralDeg);
      if (hipExtensionFullBilateral || obersNeutralBilateral) {
        isSwaybackExcluded = true;
        isAptExcluded = true;
        forceStacked = true;
        overrideAndVetoEvidence.push(
          `[OVERRIDE] SoP: full hip extension / neutral Ober's bilaterally → Stacked`
        );
      }

      const irTotal = sumDefined(sop.femoralIrLeft, sop.femoralIrRight);
      const erTotal = sumDefined(sop.femoralErLeft, sop.femoralErRight);
      if (irTotal !== null && erTotal !== null) {
        if (irTotal > erTotal) {
          isSwaybackExcluded = true;
          overrideAndVetoEvidence.push(`[VETO] SoP: femoral IR > ER → excludes Swayback`);
        } else if (erTotal > irTotal) {
          hasPlc = true;
          if (apt.pts > sb.pts && apt.pts >= 2) {
            overrideAndVetoEvidence.push(
              `[INFO] SoP: femoral ER > IR (Posterior Lower Compression) detected, but APT exclusion suppressed due to dominant visual APT markers (APT ${apt.pts} vs SB ${sb.pts})`
            );
          } else {
            isAptExcluded = true;
            overrideAndVetoEvidence.push(`[VETO] SoP: femoral ER > IR → excludes APT`);
          }
        }
      }

      const avgSlr = avgDefined(sop.slrLeft, sop.slrRight);
      if (avgSlr !== null && avgSlr > veto.slrSwaybackVetoMaxDeg) {
        isSwaybackExcluded = true;
        overrideAndVetoEvidence.push(`[VETO] SoP: SLR > ${veto.slrSwaybackVetoMaxDeg}° → excludes Swayback`);
      }
    }

    let extremeKneeFlexOverride = false;
    const mildAnteriorTiltDeg = resolveMildAnteriorTiltDeg(input.clientSex, v6.mildAnteriorTiltDeg);
    const isTibialStrong = isFinite(parsed.tibialInclination) && parsed.tibialInclination >= v6.tibialStrongDeg;
    const pelvicTiltIsAnteriorForKneeGuard =
      isFinite(pelvicTilt) && (pelvicTilt >= mildAnteriorTiltDeg && (pelvicTilt >= 5.0 || isTibialStrong));
    const tibInc = parsed.tibialInclination;
    const tibialAntIsStrong =
      isFinite(tibInc) && tibInc >= veto.tibialAntKneeFlexGuardDeg;
    const tibialIsStrongBand = isFinite(tibInc) && tibInc >= v6.tibialStrongDeg;
    const torsoIsCCurveForTibialGuard =
      isFinite(torsoOffsetCm) && torsoOffsetCm < v6.tibialGuardMinTorsoOffsetCm;
    const tibialAntIsStrongForKneeGuard =
      tibialAntIsStrong && (!torsoIsCCurveForTibialGuard || tibialIsStrongBand);
    const kneeFlexOverrideSuppressed = pelvicTiltIsAnteriorForKneeGuard || tibialAntIsStrongForKneeGuard;

    if (kneeFlex && isFinite(kneeFlexDeg) && kneeFlexDeg > veto.kneeFlexionExtremeDegMin && !kneeFlexOverrideSuppressed) {
      isAptExcluded = true;
      extremeKneeFlexOverride = true;
      overrideAndVetoEvidence.push(
        `[OVERRIDE] Image: extreme knee flexion (~${kneeFlexDeg.toFixed(0)}°) → excludes APT and boosts Swayback`
      );
    } else if (kneeFlex && isFinite(kneeFlexDeg) && kneeFlexDeg > veto.kneeFlexionExtremeDegMin && kneeFlexOverrideSuppressed) {
      overrideAndVetoEvidence.push(
        `[INFO] Image: extreme knee flexion (~${kneeFlexDeg.toFixed(0)}°) present but APT exclusion suppressed`
      );
    }

    let extremeKneeHyperextOverride = false;
    if (kneeHyperext) {
      if (isFinite(kneeHyperextDeg) && kneeHyperextDeg >= veto.kneeHyperextensionExtremeDegMin) {
        isSwaybackExcluded = true;
        extremeKneeHyperextOverride = true;
        overrideAndVetoEvidence.push(
          `[OVERRIDE] Image: extreme knee hyperextension (~${kneeHyperextDeg.toFixed(0)}°) → excludes Swayback`
        );
      } else {
        isSwaybackExcluded = true;
        overrideAndVetoEvidence.push('[VETO] Image: knee hyperextension → excludes Swayback');
      }
    }
    // Swayback exclusion if hips are not forward and trunk is not posterior (no Swayback posture definition)
    if (hipFwdCm < v6.swaybackShapeHipFwdMaxCm && torsoOffsetCm >= v6.swaybackShapeTorsoMinCm) {
      isSwaybackExcluded = true;
      overrideAndVetoEvidence.push(
        `[VETO] Image: hips not shifted forward (~${hipFwdCm.toFixed(1)} cm < ${v6.swaybackShapeHipFwdMaxCm} cm) and trunk neutral/almost-neutral (~${torsoOffsetCm.toFixed(1)} cm ≥ ${v6.swaybackShapeTorsoMinCm} cm) → excludes Swayback`
      );
    }
    // Swayback exclusion if shoulders are clearly forward of hips (anterior lean)
    if (isFinite(torsoOffsetCm) && torsoOffsetCm >= rules.strictLean) {
      isSwaybackExcluded = true;
      overrideAndVetoEvidence.push(
        `[VETO] Image: shoulders forward of hips (Δ ≈ ${torsoOffsetCm.toFixed(1)} cm ≥ ${rules.strictLean} cm) → excludes Swayback`
      );
    }

    let pelvicTiltOverride = false;
    if (isFinite(pelvicTilt)) {
      if (isPinned && pelvicTilt >= veto.pelvicTiltVetoDegMin) {
        isSwaybackExcluded = true;
        pelvicTiltOverride = true;
        overrideAndVetoEvidence.push(
          `[OVERRIDE] Image: pelvic tilt ≥ ${veto.pelvicTiltVetoDegMin}° with pinned ASIS/PSIS → excludes Swayback`
        );
      }
    }

    // --- Softened C-curve Veto & Penalty Rules ---
    const pelvicTiltDegMin =
      input.clientSex === 'Female' ? rules.pelvicTiltDegMinFemale : rules.pelvicTiltDegMinMale;
    const isKneeFlexionMild = !isFinite(kneeFlexDeg) || kneeFlexDeg < v6.kneeFlexionScoringExtremeDeg;
    const isAptTiltOrLockedKneesForVeto =
      (!kneeFlex) ||
      isKneeFlexionMild ||
      (isPinned && isFinite(pelvicTilt) && pelvicTilt >= pelvicTiltDegMin);



    if (isFinite(torsoOffsetCm)) {
      if (torsoOffsetCm > rules.strictLean) {
        isSwaybackExcluded = true;
        overrideAndVetoEvidence.push(
          `[VETO] Image: shoulders forward of hips → excludes Swayback`
        );
      } else if (torsoOffsetCm < v6.hardCCurveVetoCm && !isAptTiltOrLockedKneesForVeto) {
        isAptExcluded = true;
        overrideAndVetoEvidence.push(
          `[VETO] Image: severe "C" curve (Δ ≈ ${torsoOffsetCm.toFixed(1)} cm < ${v6.hardCCurveVetoCm} cm) → excludes APT`
        );
      } else if (torsoOffsetCm < v6.softCCurvePenaltyCm && !isAptTiltOrLockedKneesForVeto) {
        overrideAndVetoEvidence.push(
          `[PENALTY] Image: mild "C" curve (Δ ≈ ${torsoOffsetCm.toFixed(1)} cm) → −${v6.cCurvePenaltyPoints} APT points`
        );
      } else if (isAptTiltOrLockedKneesForVeto && torsoOffsetCm < v6.softCCurvePenaltyCm) {
        overrideAndVetoEvidence.push(
          `[INFO] Image: "C" curve (Δ ≈ ${torsoOffsetCm.toFixed(1)} cm) present but APT veto/penalty suppressed by locked knees or anterior tilt`
        );
      }
    }

    let finalScoreSwayback = sb.pts;
    let finalScoreApt = apt.pts;

    if (
      isFinite(torsoOffsetCm) &&
      torsoOffsetCm < v6.softCCurvePenaltyCm &&
      torsoOffsetCm >= v6.hardCCurveVetoCm &&
      !isAptTiltOrLockedKneesForVeto
    ) {
      finalScoreApt = Math.max(0, finalScoreApt - v6.cCurvePenaltyPoints);
    }

    if (extremeKneeFlexOverride) {
      finalScoreSwayback += veto.overridePointBoost;
    }
    if (extremeKneeHyperextOverride) {
      finalScoreApt += veto.overridePointBoost;
    }
    if (pelvicTiltOverride) {
      finalScoreApt += veto.overridePointBoost;
    }
    if (tibialAntIsStrongForKneeGuard) {
      finalScoreApt += veto.overridePointBoost;
      overrideAndVetoEvidence.push(
        `[BOOST] Strong tibial inclination (~${tibInc.toFixed(1)}°): +${veto.overridePointBoost} APT pts`
      );
    } else if (tibialAntIsStrong && torsoIsCCurveForTibialGuard) {
      overrideAndVetoEvidence.push(
        `[INFO] Strong tibial inclination (~${tibInc.toFixed(1)}°) present but APT boost/guard suppressed due to posterior trunk lean (~${torsoOffsetCm.toFixed(1)} cm) without tibialStrongDeg corroboration`
      );
    }

    if (isSwaybackExcluded) {
      finalScoreSwayback = 0;
    }
    if (isAptExcluded) {
      finalScoreApt = 0;
    }

    let orientation: PostureOrientationLabel = 'Stacked';
    let matched = 0;
    let winnerEvidence: string[] = [];
    let decisionPath = '';

    if (forceStacked || (finalScoreSwayback === 0 && finalScoreApt === 0)) {
      orientation = 'Stacked';
      matched = 0;
      winnerEvidence = forceStacked
        ? []
        : ['No surviving swayback vs anterior-orientation points after exclusions.'];
      decisionPath = forceStacked ? 'veto.forceStacked' : 'noSurvivingPoints.stacked';
    } else if (finalScoreSwayback > finalScoreApt) {
      orientation = 'Swayback';
      matched = finalScoreSwayback;
      winnerEvidence = sb.evidence;
      decisionPath = 'sbSurvives';
    } else if (finalScoreApt > finalScoreSwayback) {
      if (hasPlc || isPlcHeadKneePattern) {
        orientation = 'Anterior Pelvic Tilt with Posterior Lower Compression';
        winnerEvidence = [
          ...apt.evidence,
          '[CLASSIFICATION] Upgraded to Anterior Pelvic Tilt with Posterior Lower Compression due to combined anterior tilt and posterior lower compression indicators.',
        ];
      } else {
        orientation = 'Anterior Pelvic Tilt';
        winnerEvidence = apt.evidence;
      }
      matched = finalScoreApt;
      decisionPath = 'aptSurvives';
    } else {
      if (hipFwdCm >= rules.hipForwardCmMin && finalScoreSwayback > 0) {
        orientation = 'Swayback';
        matched = finalScoreSwayback;
        winnerEvidence = [...sb.evidence, 'Tie-break: large hip–ankle offset favors swayback shift.'];
        decisionPath = 'tieBreak.hipFwdFavorsSwayback';
      } else {
        if (hasPlc || isPlcHeadKneePattern) {
          orientation = 'Anterior Pelvic Tilt with Posterior Lower Compression';
          winnerEvidence = [
            ...apt.evidence,
            'Tie-break: hip stack not dominant → anterior orientation.',
            '[CLASSIFICATION] Upgraded to Anterior Pelvic Tilt with Posterior Lower Compression due to combined anterior tilt and posterior lower compression indicators.',
          ];
        } else {
          orientation = 'Anterior Pelvic Tilt';
          winnerEvidence = [...apt.evidence, 'Tie-break: hip stack not dominant → anterior orientation.'];
        }
        matched = finalScoreApt;
        decisionPath = 'tieBreak.hipStackFavorsApt';
      }
    }

    const evidence = [...overrideAndVetoEvidence, ...winnerEvidence];

    const confidencePct =
      orientation === 'Stacked'
        ? Math.min(
            100,
            Math.round(
              (1 - Math.max(finalScoreSwayback, finalScoreApt) / Math.max(1, dynamicMax * 2)) *
                100
            )
          )
        : Math.min(100, Math.round((matched / dynamicMax) * 100));

    logPostureClassificationDebug(DEBUG_LOG_PREFIX, 'adaptiveThresholdVprOverrideV6 classification', {
      rawScoreSwayback: sb.pts,
      rawScoreApt: apt.pts,
      finalScoreSwayback,
      finalScoreApt,
      isSwaybackExcluded,
      isAptExcluded,
      forceStacked,
      orientation,
      decisionPath,
      hasSopMetrics: !!input.sopMetrics,
      vetoEvidence: overrideAndVetoEvidence,
    });

    return {
      result: {
        orientation,
        confidencePct,
        matchedPoints: matched,
        maxPoints: dynamicMax,
        strategyId: STRATEGY_ID,
        strategyDisplayName: STRATEGY_NAME,
        evidence,
      },
      finalScoreSwayback,
      finalScoreApt,
    };
}

function orientationFamily(orientation: PostureOrientationLabel): 'Swayback' | 'APT' | 'Stacked' {
  if (orientation === 'Swayback') return 'Swayback';
  if (orientation === 'Stacked') return 'Stacked';
  return 'APT';
}

function sliceAnalysisToLateralView(
  input: PostureClassificationStrategyInput,
  view: 'Left' | 'Right'
): PostureClassificationStrategyInput {
  const analysisData = { ...input.analysisData };
  if (view === 'Left') {
    delete analysisData.Right;
  } else {
    delete analysisData.Left;
  }
  const sagittal =
    input.sagittal?.sideView === view ? input.sagittal : null;
  return {
    ...input,
    analysisData,
    sagittal,
  };
}

function classifyCore(
  input: PostureClassificationStrategyInput,
  context: PostureClassificationStrategyContext
): CoreClassification | null {
  return classifyFusedView(input, context);
}

function applyBilateralConsensus(
  input: PostureClassificationStrategyInput,
  context: PostureClassificationStrategyContext
): PostureClassificationStrategyResult | null {
  const fused = classifyCore(input, context);
  if (!fused) return null;

  const hasLeft = !!input.analysisData.Left;
  const hasRight = !!input.analysisData.Right;
  if (!hasLeft || !hasRight) {
    return fused.result;
  }

  const left = classifyCore(sliceAnalysisToLateralView(input, 'Left'), {
    ...context,
    asisPsisLine: input.sagittal?.sideView === 'Left' ? context.asisPsisLine : null,
  });
  const right = classifyCore(sliceAnalysisToLateralView(input, 'Right'), {
    ...context,
    asisPsisLine: input.sagittal?.sideView === 'Right' ? context.asisPsisLine : null,
  });

  if (!left || !right) {
    return fused.result;
  }

  const leftFamily = orientationFamily(left.result.orientation);
  const rightFamily = orientationFamily(right.result.orientation);

  if (leftFamily === rightFamily) {
    const fusedFamily = orientationFamily(fused.result.orientation);
    const agreementLine =
      leftFamily === fusedFamily
        ? `[INFO] Left and Right sagittal views agree on ${leftFamily} (Left ${left.result.orientation}, Right ${right.result.orientation})`
        : `[INFO] Left and Right sagittal views agree on ${leftFamily} (Left ${left.result.orientation}, Right ${right.result.orientation}); using fused bilateral landmarks (${fusedFamily})`;
    return {
      ...fused.result,
      evidence: [agreementLine, ...fused.result.evidence],
    };
  }

  const fusedFamily = orientationFamily(fused.result.orientation);
  const leftMargin = Math.abs(left.finalScoreApt - left.finalScoreSwayback);
  const rightMargin = Math.abs(right.finalScoreApt - right.finalScoreSwayback);
  const supportingView =
    leftFamily === fusedFamily && rightFamily !== fusedFamily
      ? 'Left'
      : rightFamily === fusedFamily && leftFamily !== fusedFamily
        ? 'Right'
        : leftMargin >= rightMargin
          ? 'Left'
          : 'Right';

  return {
    ...fused.result,
    evidence: [
      `[INFO] Left and Right sagittal views disagree (Left: ${left.result.orientation}, Right: ${right.result.orientation}); using fused bilateral metrics (${fusedFamily}, supported by ${supportingView} view)`,
      ...fused.result.evidence,
    ],
  };
}

export const adaptiveThresholdVprOverrideV6Strategy: PostureOrientationClassificationStrategy = {
  id: STRATEGY_ID,
  displayName: STRATEGY_NAME,
  maxClassificationPoints: MAX_AUTO_POINTS,
  classify: applyBilateralConsensus,
};
